import React from "react";
import Card from "../../components/Card";
import BaseLayout from "../../components/Layout";
import { actionGetDashboardGeral } from "../../features/dashboard-geral/slices";
import connectSocket from "../../helpers/connect-socket";
import {
  factoryChart,
  factoryLabels,
  optionsChart,
} from "../../helpers/factory-charts";
import { objectIsEmpty } from "../../helpers/validators";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import useIsMobile from "../../hooks/useIsMobile";
import "./styles.scss";
import { getUser } from "../../helpers/user";
import MapaGoogle from "../../components/MapaGoogle";
import { actionGetAlertasAbertos } from "../../features/alertas/slices";
import { actionGetAlertasProperty } from "../../features/alertas/getAll-alertas/slices";
import {
  ChevronDown,
  Map as MapIcon,
  AlertTriangle,
  PieChart as PieChartIcon,
} from "lucide-react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

const CORES_PIZZA = [
  "#e74c3c",
  "#3498db",
  "#2ecc71",
  "#f39c12",
  "#9b59b6",
  "#1abc9c",
  "#34495e",
  "#e84393",
  "#7f8c8d",
  "#2980b9",
];

const optionsPizzaEquipamentos = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "right" as const,
      labels: { boxWidth: 12, font: { size: 11 } },
    },
  },
};

enum ELablesPlanosDeAcao {
  "cardReconhecido" = "Alerta Reconhecido",
  "cardEmAndamento" = "Plano de ação em Andamento",
  "cardEmAndamentoAtrasado" = "Plano de ação em Atrasado",
  "cardAltOkAguardInfo" = "Finalizado aguardando informação",
  "cardAltNaoVizualizado" = "Não visualizado no prazo",
  "cardAltReconhecido" = "Reconhecido no prazo",
  "cardPlanoAcaoPrazoOk" = "Plano de ação executado no prazo",
  "cardPrazoExcedido" = "Prazo Excedido",
}

const ListPlanosDeAcao = ({ data }) => {
  const newData = Object.entries(data);
  return (
    <div className="list-planos-acao">
      {newData.map((item: any, index: number) => (
        <div key={index} className="list-planos-acao__item">
          <p>{ELablesPlanosDeAcao[item[0]]}</p>
          <p>
            <strong>{item[1]}</strong>
          </p>
        </div>
      ))}
    </div>
  );
};

const initialState = {
  cardNaoVisualizado: 0,
  cardReconhecido: 0,
  cardPlanoAcao: 0,
  cardPrazoExcedido: 0,
  cardProgramado: 0,
  cardTotalLojas: 0,
  cardConfiabilidade: 0,
  graficoAlertas: { datasets: [], labels: [] },
  graficoCards: { datasets: [], labels: [] },
  graficoDisponibilidade: { datasets: [], labels: [] },
  lastAlertas: [],
  lojasAlertas: [],
  ocorrenciaConsolidadas: [],
  cardDashboardAlertas: [],
  statusBrasil: {
    ba: "default",
    se: "default",
    pe: "default",
    al: "default",
    am: "default",
    pa: "default",
    mt: "default",
    ro: "default",
    ac: "default",
    ap: "default",
    rj: "default",
    rs: "default",
    sc: "default",
    pr: "default",
    sp: "default",
    ms: "default",
    go: "default",
    mg: "default",
    es: "default",
    pi: "default",
    ce: "default",
    rr: "default",
    to: "default",
    ma: "default",
    rn: "default",
    pb: "default",
    df: "default",
  },
  lojasMapa: [],
};

type DashboardTab = "mapa" | "alertas" | "equipamentos";

// Altura reservada pra barra de pílulas flutuante + respiro embaixo dela,
// descontada do espaço disponível pro conteúdo.
const PILLS_RESERVED_HEIGHT = 96;
const MIN_CONTENT_HEIGHT = 340;

const PageDashboard = () => {
  const user = getUser();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const getAlertasAbertos = useAppSelector((state) => state.getAlertasAbertos);
  const { getDashboardGeral } = useAppSelector((state) => state);
  const { data: metrics } = useAppSelector((state) => state.getAlertasProperty);

  const [expandedIds, setExpandedIds] = React.useState<number[]>([]);
  const [state, setState] = React.useState(initialState);
  const [activeTab, setActiveTab] = React.useState<DashboardTab>("mapa");

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState<number | null>(null);

  // ─── Trava o scroll da página inteira enquanto o dashboard está montado ───
  // Isso garante que a scrollbar do navegador nunca aparece nessa página,
  // não importa o que aconteça dentro dela (é restaurado ao sair da página).
  React.useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);

  // ─── Mede exatamente o espaço vertical disponível pro conteúdo ───
  // Em vez de tentar adivinhar a altura do header/sidebar do BaseLayout via
  // CSS, medimos a posição real do topo do conteúdo na tela e calculamos o
  // que sobra até o fim da viewport (menos o espaço da barra de pílulas).
  React.useEffect(() => {
    const updateHeight = () => {
      if (!contentRef.current) return;
      const top = contentRef.current.getBoundingClientRect().top;
      const available = window.innerHeight - top - PILLS_RESERVED_HEIGHT;
      setContentHeight(Math.max(available, MIN_CONTENT_HEIGHT));
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const getAlertStatusConfig = (data: any) => {
    const agora = new Date();
    const dataNormalizacao = data.dataNormalizacao
      ? new Date(data.dataNormalizacao)
      : null;
    const dataPrevisao = data.dataPrevisaoPlanoAcao
      ? new Date(data.dataPrevisaoPlanoAcao)
      : null;

    const isNormalizado =
      dataNormalizacao !== null && dataNormalizacao.getTime() > 0;

    const prazoExcedido =
      data.planoAcaoAtivo &&
      !isNormalizado &&
      dataPrevisao &&
      dataPrevisao < agora;

    if (prazoExcedido)
      return {
        label: "AVISO - PRAZO EXCEDIDO",
        colorClass: "status-vermelho",
        priority: 1,
      };

    if (!data.reconhecido && !isNormalizado)
      return {
        label: "AVISO - ALERTA ATUADO",
        colorClass: "status-vermelho",
        priority: 2,
      };

    const planoPendente =
      data.planoAcaoAtivo && (!data.planoAcao || data.planoAcao.trim() === "");

    if (data.reconhecido || planoPendente) {
      if (!isNormalizado || planoPendente) {
        return {
          label: "ATENÇÃO - EM ANDAMENTO",
          colorClass: "status-laranja",
          priority: 3,
        };
      }
    }

    return { label: "RESOLVIDO", colorClass: "status-verde", priority: 4 };
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const eventDate = new Date(dateString);
    const diffInSeconds = Math.floor(
      (now.getTime() - eventDate.getTime()) / 1000,
    );
    if (diffInSeconds < 60) return "agora mesmo";
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `há ${diffInMinutes} min`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `há ${diffInHours} ${diffInHours === 1 ? "hora" : "horas"}`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `há ${diffInDays} ${diffInDays === 1 ? "dia" : "dias"}`;
  };

  const toggleExpanded = (id: number) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );

  const createGraph = React.useCallback(
    (data: any, origem = "desconhecida") => {
      if (objectIsEmpty(data)) return;

      console.log(
        `[createGraph] origem=${origem}, lojas=${data.lojasMapa?.length}, cardTotalLojas=${data.cardTotalLojas}`,
      );

      setState((prev) => {
        const isSameData =
          prev.cardTotalLojas === data.cardTotalLojas &&
          prev.lojasMapa?.length === data.lojasMapa?.length;

        if (isSameData) return prev;

        const statusBrasil =
          data.mapaBrasil?.reduce(
            (acc: any, item: any) => ({
              ...acc,
              [item.sigla.toLowerCase()]: item.status,
            }),
            {},
          ) || {};

        return {
          ...prev,
          cardNaoVisualizado: data.cardNaoVisualizado,
          cardReconhecido: data.cardReconhecido,
          cardPlanoAcao: data.cardPlanoAcao,
          cardPrazoExcedido: data.cardPrazoExcedido,
          cardProgramado: data.cardProgramado,
          cardTotalLojas: data.cardTotalLojas,
          cardConfiabilidade: data.cardConfiabilidade,
          ocorrenciaConsolidadas: data.ocorrenciaConsolidadas,
          lastAlertas: data.lastAlertas,
          cardDashboardAlertas: data.cardDashboardAlertas,
          graficoAlertas: {
            datasets: factoryChart(
              data.dsGrafHistoAlertas,
              data.grafHistoAlertasColors,
            ),
            labels: factoryLabels(data.grafHistoAlertasLabels),
          },
          graficoCards: {
            datasets: factoryChart(
              data.dsGrafCards,
              data.grafCardsColors,
              "pie",
            ),
            labels: data.grafCardsLabels,
          },
          graficoDisponibilidade: {
            datasets: factoryChart(data.dsGrafDisp, data.grafDispColors, "pie"),
            labels: data.grafDispLabels,
          },
          statusBrasil: { ...prev.statusBrasil, ...statusBrasil },
          lojasMapa: data.lojasMapa || [],
        };
      });
    },
    [],
  );

  const alertasAgrupadosPorLoja = React.useMemo(() => {
    if (!getAlertasAbertos?.data) return {};

    const nivelUsuario = user?.usuario?.nivel;
    const idRegionalUsuario = user?.usuario?.idRegional;

    const ID_REGIONAL_AMBIENTE_VALIDACAO = 11;

    const alertasFiltrados = getAlertasAbertos.data
      .map((item) => ({ ...item, config: getAlertStatusConfig(item) }))
      .filter((item) => {
        if (item.config.priority > 3) return false;

        if (nivelUsuario < 7 && item.loja?.idRegional !== idRegionalUsuario) {
          return false;
        }

        if (
          item.loja?.idRegional === ID_REGIONAL_AMBIENTE_VALIDACAO &&
          nivelUsuario !== 9
        ) {
          return false;
        }

        const desc = item.descricao.toLowerCase();
        const isTecnico =
          desc.includes("encontra-se offline") ||
          desc.includes("módulo") ||
          desc.includes("central id:");

        if (isTecnico && nivelUsuario !== 999) return false;

        return true;
      });

    return alertasFiltrados.reduce((acc: any, alerta: any) => {
      const nomeLoja = alerta.loja?.nome || "Loja não identificada";
      if (!acc[nomeLoja]) acc[nomeLoja] = [];
      acc[nomeLoja].push(alerta);
      return acc;
    }, {});
  }, [getAlertasAbertos.data, user?.usuario?.nivel, user?.usuario?.idRegional]);

  const totalAlertasCriticos = React.useMemo(
    () =>
      Object.values(alertasAgrupadosPorLoja).reduce(
        (acc: number, arr: any) => acc + arr.length,
        0,
      ),
    [alertasAgrupadosPorLoja],
  );

  const alertasPorEquipamento = metrics?.alertasPorEquipamento ?? [];

  const dataEquipamento = React.useMemo(
    () => ({
      labels: alertasPorEquipamento.map((e: any) => e.nomeEquipamento),
      datasets: [
        {
          data: alertasPorEquipamento.map((e: any) => e.quantidade),
          backgroundColor: CORES_PIZZA,
          borderWidth: 0,
        },
      ],
    }),
    [alertasPorEquipamento],
  );

  React.useEffect(() => {
    dispatch(actionGetDashboardGeral());
    dispatch(
      actionGetAlertasAbertos({
        idLoja: 0,
        idCliente: user.usuario.idCliente,
        idRegional: 0,
      }),
    );
    dispatch(
      actionGetAlertasProperty({
        idLoja: 0,
        idCliente: user.usuario.idCliente,
      }),
    );
  }, []);

  React.useEffect(() => {
    if (getDashboardGeral.data) {
      createGraph(getDashboardGeral.data, "redux-http");
    }
  }, [getDashboardGeral.data, createGraph]);

  React.useEffect(() => {
    console.log("user: ", user);
    // No useEffect de conexão do socket
    const connection = connectSocket({
      url: "hubGlobalCliente",
      hub: "GlobalDadosCliente",
      cb: (data: any) => createGraph(data, "socket"),
      params: {
        access_token: user.token,
        // Passa a primeira regional ou a lista se o hub suportar
        idRegional:
          user?.usuario?.idsRegionaisVinculadas?.[0] ??
          user?.usuario?.idRegional ??
          0,
      },
    });

    return () => {
      if (connection?.stop) void connection.stop();
    };
  }, []);

  console.log("Alertas Abertos: ", getAlertasAbertos.data);
  console.log("User: ", user);

  const tabs: {
    id: DashboardTab;
    label: string;
    icon: React.ReactNode;
    badge?: number;
  }[] = [
    { id: "mapa", label: "Mapa", icon: <MapIcon size={20} /> },
    {
      id: "alertas",
      label: "Alertas",
      icon: <AlertTriangle size={20} />,
      badge: totalAlertasCriticos > 0 ? totalAlertasCriticos : undefined,
    },
    {
      id: "equipamentos",
      label: "Equipamentos",
      icon: <PieChartIcon size={20} />,
    },
  ];

  const contentStyle: React.CSSProperties = {
    height: contentHeight ? `${contentHeight}px` : "auto",
  };

  return (
    <BaseLayout>
      <div className="dashboard-fixed">
        {/*
          Cada seção só existe na árvore enquanto está ativa — trocar de
          pílula desmonta completamente a anterior (incluindo o mapa, que
          nunca fica escondido via display:none, ele deixa de existir).
        */}
        <div
          className="dashboard-fixed__content"
          ref={contentRef}
          style={contentStyle}
        >
          {activeTab === "mapa" && (
            <Card className="card-mapa card-mapa--fill">
              <p className="mb-20">Mapa do Brasil</p>
              <div className="card-mapa__map-wrapper">
                <MapaGoogle
                  status={state.statusBrasil}
                  lojasMapa={state.lojasMapa}
                  zoom={4}
                  tipoMapa="roadmap"
                  tema="light"
                  height="100%"
                />
              </div>
            </Card>
          )}

          {activeTab === "alertas" && (
            <Card className="card-alertas card-alertas--fill">
              <div className="card-alertas__header">
                <p className="mb-20">Alertas Críticos por Loja</p>
              </div>

              <div className="alert-list scroll-dashboard">
                {Object.keys(alertasAgrupadosPorLoja).length > 0 ? (
                  Object.entries(alertasAgrupadosPorLoja).map(
                    ([nomeLoja, alertas]: [string, any]) => (
                      <div
                        key={nomeLoja}
                        className="loja-group-wrapper"
                        style={{ marginBottom: "20px" }}
                      >
                        <div
                          className="loja-group-header"
                          style={{
                            padding: "10px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            marginBottom: "10px",
                            borderLeft: "4px solid #e74c3c",
                            display: "flex",
                            justifyContent: "space-between",
                            fontWeight: "bold",
                          }}
                        >
                          <span>{nomeLoja.toUpperCase()}</span>
                          <span style={{ color: "#e74c3c" }}>
                            ({alertas.length})
                          </span>
                        </div>

                        {alertas.map((data: any) => (
                          <div
                            key={data.id}
                            className={`alert-list__item ${data.config.colorClass}`}
                            style={{ marginBottom: "8px" }}
                          >
                            <div className="header-row">
                              <section className="status">
                                <span
                                  className="status-dot"
                                  data-status={data.config.label}
                                />
                                <span className="id-text">
                                  Alerta #{data.id} —{" "}
                                  {formatTimeAgo(data.dataEvento)}
                                </span>
                              </section>
                              {(data.procedimento || data.descricaoAlerta) && (
                                <ChevronDown
                                  size={18}
                                  className={`expand-icon ${expandedIds.includes(data.id) ? "active" : ""}`}
                                  onClick={() => toggleExpanded(data.id)}
                                />
                              )}
                            </div>

                            <section className="content-row">
                              <p className="desc-text">{data.descricao}</p>
                              <div className="info-footer">
                                <span className="date-text">
                                  {new Date(data.dataEvento).toLocaleString(
                                    "pt-br",
                                    {
                                      dateStyle: "short",
                                      timeStyle: "short",
                                    },
                                  )}
                                </span>
                              </div>
                            </section>

                            <div
                              className={`description__info ${expandedIds.includes(data.id) ? "expanded" : ""}`}
                            >
                              <div className="info-box">
                                <strong>Procedimento:</strong>
                                <p>
                                  {data.procedimento || "Nenhum procedimento."}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                  )
                ) : (
                  <p className="text-center color-disabled">
                    Nenhum alerta crítico no momento.
                  </p>
                )}
              </div>
            </Card>
          )}

          {activeTab === "equipamentos" && (
            <Card className="card-pizza card-pizza--fill">
              <div className="card-pizza__header">
                <p>Top Equipamentos com Falha (30 dias)</p>
              </div>
              <div className="card-pizza__body">
                {alertasPorEquipamento.length > 0 ? (
                  <Pie
                    options={optionsPizzaEquipamentos}
                    data={dataEquipamento}
                  />
                ) : (
                  <p className="text-center color-disabled">
                    Sem dados de equipamentos no período.
                  </p>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="dashboard-pills" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`dashboard-pills__btn ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="dashboard-pills__icon">
                {tab.icon}
                {typeof tab.badge === "number" && (
                  <span className="dashboard-pills__badge">{tab.badge}</span>
                )}
              </span>
              <span className="dashboard-pills__label">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </BaseLayout>
  );
};

export default PageDashboard;
